import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { OpsStateBanner } from '../OpsStateBanner';

type IncidentToolbarProps = {
  canUseOpsAutomation: boolean;
  opsRecoveryError: string | null;
  opsRecoveryResult: any | null;
  clearOpsRecovery: () => void;
  scopeDialogOpen: boolean;
  closeScopeDialog: () => void;
  scopeOrgDraft: string;
  setScopeOrgDraft: (value: string) => void;
  scopeSiteDraft: string;
  setScopeSiteDraft: (value: string) => void;
  saveScope: () => void;
  error: string | null;
  clearError: () => void;
  opsStateResult: {
    state: any;
    reasons: any[];
  };
  opsBannerHeadline?: string;
  diagnosticsHref: string;
  copyContext: () => void;
  operatorMode?: boolean;
};

const IncidentToolbar: React.FC<IncidentToolbarProps> = ({
  canUseOpsAutomation,
  opsRecoveryError,
  opsRecoveryResult,
  clearOpsRecovery,
  scopeDialogOpen,
  closeScopeDialog,
  scopeOrgDraft,
  setScopeOrgDraft,
  scopeSiteDraft,
  setScopeSiteDraft,
  saveScope,
  error,
  clearError,
  opsStateResult,
  opsBannerHeadline,
  diagnosticsHref,
  copyContext,
  operatorMode = false,
}) => {
  return (
    <>
      {canUseOpsAutomation && (opsRecoveryError || opsRecoveryResult) && (
        <Alert
          severity={opsRecoveryError ? 'error' : 'success'}
          sx={{ mb: 2 }}
          onClose={clearOpsRecovery}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {opsRecoveryError ? 'Ticketing recovery failed' : 'Ticketing recovery complete'}
          </Typography>
          {opsRecoveryError ? (
            <Typography variant="body2">{opsRecoveryError}</Typography>
          ) : (
            <pre style={{ margin: 0, marginTop: 6, whiteSpace: 'pre-wrap', fontSize: 12 }}>
              {JSON.stringify(opsRecoveryResult, null, 2)}
            </pre>
          )}
        </Alert>
      )}

      <Dialog open={scopeDialogOpen} onClose={closeScopeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Set org/site scope</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This controls which incidents you see. For single-scope users it&apos;s auto-set, but you can override it here.
          </Alert>
          <TextField
            fullWidth
            label="org_id"
            value={scopeOrgDraft}
            onChange={(e) => setScopeOrgDraft(e.target.value)}
            margin="dense"
            placeholder="e.g. acme"
          />
          <TextField
            fullWidth
            label="site_id"
            value={scopeSiteDraft}
            onChange={(e) => setScopeSiteDraft(e.target.value)}
            margin="dense"
            placeholder="e.g. yyz"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeScopeDialog}>Cancel</Button>
          <Button variant="contained" onClick={saveScope}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {operatorMode ? null : (
      <OpsStateBanner
        state={opsStateResult.state}
        reasons={opsStateResult.reasons}
        headline={opsBannerHeadline}
        primaryCta={{ label: 'Go to Diagnostics', to: diagnosticsHref }}
        secondaryCta={{
          label: 'Copy context',
          onClick: copyContext,
        }}
      />
      )}
    </>
  );
};

export default IncidentToolbar;
